import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Layers,
  Building2,
  Shield,
  Award,
  ClipboardCheck,
  Truck,
  FileCheck,
  Globe,
  Phone,
  Handshake,
} from "lucide-react";
import { ChevronRight } from "../components/DirectionalIcon";
import { buildLocalizedPath } from "../constants/languages";
import StatCounter from "../components/StatCounter";
import ComplianceBadge from "../components/ComplianceBadge";
import ProcessStep from "../components/ProcessStep";
import FAQItem from "../components/FAQItem";

import rooftopScale from "../assets/rooftop-scale.jpg";
import containerLoading from "../assets/container-loading.jpg";
import yardTrailerBlurred from "../assets/yard-trailer-blurred.jpg";
import yardTankGroundlevel from "../assets/yard-tank-groundlevel.jpg";

// English-only, like PrivacyPage/TermsPage — this is institutional/B2B
// content aimed at UK corporates, EPC contractors, and asset managers, not
// the retail buy/sell audience the rest of the site's 13 translations serve.
// Reachable via the homepage banner and direct link only — no main nav or
// footer entry (explicit decision, not an oversight).
const CorporatePartnersPage = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.title = "Sell Surplus Solar Panels UK | Corporate & Utility-Scale Buyer | M A Galaxy";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "M A Galaxy International partners with UK corporates, EPC contractors, and asset managers on the disposal, collection, and export of surplus and decommissioned solar PV assets."
      );
    }
  }, []);

  const trackRecordStats = [
    { value: 60000, suffix: "+", label: "Solar Panels Processed for UK Corporate Partners in 2026", icon: Layers, highlight: true },
    { value: 2021, suffix: "", separator: "", label: "UK-Registered Entity — Co. No. 13671542, VAT GB425010549", icon: Building2 },
    { value: 2029, suffix: "", separator: "", label: "Upper Tier Waste Carrier Registration Valid Until (CBDU628298)", icon: Shield },
    { value: 2000, suffix: "", separator: "", label: "Trading in the Global Solar Sector Since", icon: Award },
  ];

  const handleCards = [
    {
      icon: ClipboardCheck,
      title: "Site Assessment & Valuation",
      description: "Site assessment and formal valuation.",
    },
    {
      icon: Truck,
      title: "Collection & Logistics",
      description: "Collection, palletization, and logistics coordination.",
    },
    {
      icon: FileCheck,
      title: "Export Documentation & Compliance",
      description: "Full export documentation and compliance tracking.",
    },
    {
      icon: Globe,
      title: "Secondary Market Access",
      description: "Direct access to established secondary markets across Pakistan, the Middle East, and Africa — meaning no idle inventory sitting in a yard.",
    },
  ];

  const steps = [
    { icon: Phone, title: "Enquiry & Site Details", description: "Tell us about the site, the panels, and the timeline." },
    { icon: ClipboardCheck, title: "Site Assessment & Quote", description: "We assess the inventory and provide a formal quote." },
    { icon: Handshake, title: "Agreement & Collection Schedule", description: "Terms are agreed and a collection schedule is set." },
    { icon: Truck, title: "Collection & Export", description: "Panels are collected, documented, and exported under full compliance." },
  ];

  const faqs = [
    {
      question: "How is payment structured?",
      answer: "Terms are agreed case-by-case, structured around what your organisation's procurement and finance teams require — including phased payments or other arrangements where needed.",
    },
    {
      question: "Who's liable for the panels during collection and transport?",
      answer: "Liability transfers to M A Galaxy the moment panels are loaded at your site (EXW terms) — once collection begins, it's no longer your organisation's risk to manage.",
    },
    {
      question: "How is the disposal/export process tracked for compliance purposes?",
      answer: "M A Galaxy operates under an Upper Tier Waste Carrier registration with the Environment Agency (CBDU628298), and all export shipments are handled under full customs and trade documentation via our registered EORI.",
    },
    {
      question: "Do you handle single-site jobs or only large lots?",
      answer: "Both — from single-site decommissioning through to multi-megawatt farm clearance.",
    },
  ];

  // FAQPage structured data, generated from the same `faqs` array rendered
  // below so the two can never drift apart. Injected/removed on mount rather
  // than living in index.html since it's specific to this one page's
  // content, not sitewide like the Organization schema there.
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  return (
    <div data-testid="corporate-partners-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${rooftopScale})` }}
        >
          <div className="absolute inset-0 hero-gradient"></div>
        </div>

        <div className="relative z-10 container-custom text-center pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-gold text-xs uppercase tracking-[0.3em] font-semibold mb-6">
              Corporate Partners
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 max-w-4xl mx-auto">
              We Purchase Used, Surplus &amp; Decommissioned Solar Panels — Corporate &amp; Utility-Scale Asset Recovery
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              M A Galaxy International partners with UK corporates, EPC contractors, and asset managers to manage
              the disposal, collection, and export of surplus and decommissioned solar PV assets — from single-site
              repowering to multi-megawatt farm clearance.
            </p>

            <Link
              to={buildLocalizedPath("/contact", i18n.language)}
              data-testid="hero-corporate-quote-btn"
              className="btn-magnetic inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
            >
              Request a Corporate Quote
              <ChevronRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Track Record Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">Track Record</span>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-navy mt-4">
              Built on Verifiable Compliance
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            {trackRecordStats.map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <StatCounter {...stat} />
              </motion.div>
            ))}
          </div>
          <p className="text-slate-500 text-sm text-center mb-16">
            Registered EORI number on file for all international export documentation.
          </p>

          <motion.img
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src={yardTrailerBlurred}
            alt="M A Galaxy International trailer loaded with palletized panels at the operational yard"
            className="w-full max-w-4xl mx-auto rounded-sm border border-slate-200"
            loading="lazy"
          />
        </div>
      </section>

      {/* What We Handle Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16">
            <div>
              <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">What We Handle</span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4 mb-6">
                Full-Scope Asset Recovery, End to End
              </h2>
              <p className="text-slate-600 leading-relaxed">
                From the first site visit to the shipment leaving UK customs, MA Galaxy manages every stage of the
                process — so your team isn't coordinating multiple contractors or chasing paperwork.
              </p>
            </div>
            <motion.img
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              src={containerLoading}
              alt="Shipping container being loaded with palletized solar panels for export"
              className="w-full rounded-sm border border-slate-200"
              loading="lazy"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {handleCards.map((card) => (
              <ComplianceBadge key={card.title} {...card} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">How It Works</span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              Four Steps, Start to Finish
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div className="max-w-xl">
              {steps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ProcessStep number={index + 1} stepLabel={`Step ${index + 1}`} {...step} />
                </motion.div>
              ))}
            </div>
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              src={yardTankGroundlevel}
              alt="Ground-level view of M A Galaxy International's operational yard"
              className="w-full rounded-sm border border-slate-200"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">FAQ</span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              Common Questions From Corporate Partners
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <FAQItem key={index} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-navy text-white relative noise-overlay">
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">Ready to Discuss Your Site?</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-10">
            Whether it's a single-site decommissioning or a multi-megawatt farm clearance, get a formal quote for
            your inventory today.
          </p>
          <Link
            to={buildLocalizedPath("/contact", i18n.language)}
            data-testid="corporate-cta-btn"
            className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
          >
            Request a Corporate Quote
            <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CorporatePartnersPage;
